import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UserService } from '../Services/user.service';
import { ActivationJourneyService } from './activation-journey.service';
import { OnboardingChecklistService } from './onboarding-checklist.service';
import { Country, getCountriesList } from '../countriesConfig';

export type ContactPhoneSource =
  | 'ready'
  | 'private_proof'
  | 'fair_setup'
  | 'daily_popup'
  | 'assisted_setup';

export interface ContactPhoneParts {
  dialCode: string;
  national: string;
}

@Injectable({ providedIn: 'root' })
export class ContactPhoneCaptureService {
  readonly defaultDialCode = '+91';

  constructor(
    private userService: UserService,
    private onboardingService: OnboardingChecklistService,
    private activationJourney: ActivationJourneyService
  ) {}

  getCountries(): Country[] {
    const list = getCountriesList().map((c) => ({
      ...c,
      dialCode: this.normalizeDialCode(c.dialCode),
    }));
    const preferred = ['IN', 'US', 'AE', 'SG', 'AU', 'GB', 'CA', 'DE', 'PH', 'LK'];
    const preferredSet = new Set(preferred);
    const top = preferred
      .map((code) => list.find((c) => c.code === code))
      .filter((c): c is Country => !!c);
    const rest = list
      .filter((c) => !preferredSet.has(c.code))
      .sort((a, b) => a.name.localeCompare(b.name));
    // Dedupe by dialCode keeping first (preferred) entry.
    const seen = new Set<string>();
    const deduped: Country[] = [];
    for (const c of [...top, ...rest]) {
      if (seen.has(c.dialCode)) {
        continue;
      }
      seen.add(c.dialCode);
      deduped.push(c);
    }
    return deduped;
  }

  normalizeDialCode(dialCode: string): string {
    const raw = String(dialCode || '').trim();
    if (!raw) {
      return this.defaultDialCode;
    }
    const cleaned = raw.replace(/[^\d+]/g, '');
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  }

  normalizeNational(national: string): string {
    let digits = String(national || '').replace(/\D/g, '');
    // Common local habit: leading 0 with country code selected.
    if (digits.startsWith('0')) {
      digits = digits.replace(/^0+/, '');
    }
    return digits;
  }

  composeE164(dialCode: string, national: string): string {
    const dial = this.normalizeDialCode(dialCode);
    const local = this.normalizeNational(national);
    if (!local) {
      return '';
    }
    return `${dial}${local}`;
  }

  parsePhoneParts(value: unknown): ContactPhoneParts {
    const raw = this.normalizePhone(value);
    if (!raw) {
      return { dialCode: this.defaultDialCode, national: '' };
    }

    const countries = this.getCountries()
      .map((c) => ({
        ...c,
        dialNorm: this.normalizeDialCode(c.dialCode),
      }))
      .sort((a, b) => b.dialNorm.length - a.dialNorm.length);

    const compact = raw.replace(/[^\d+]/g, '');
    const withPlus = compact.startsWith('+') ? compact : `+${compact.replace(/\D/g, '')}`;

    for (const country of countries) {
      if (withPlus.startsWith(country.dialNorm)) {
        return {
          dialCode: country.dialNorm,
          national: withPlus.slice(country.dialNorm.length).replace(/\D/g, ''),
        };
      }
    }

    // Fallback: treat whole digit string as national under default dial.
    return {
      dialCode: this.defaultDialCode,
      national: withPlus.replace(/\D/g, ''),
    };
  }

  normalizePhone(value: unknown): string {
    if (value == null) {
      return '';
    }
    const raw = String(value).trim();
    if (!raw || raw === 'undefined' || raw === 'none' || raw.toLowerCase() === 'null-undefined') {
      return '';
    }
    return raw;
  }

  isValidNational(national: string, dialCode: string = this.defaultDialCode): boolean {
    const rawDigits = String(national || '').replace(/\D/g, '');
    if (!rawDigits) {
      return true;
    }
    const local = this.normalizeNational(national);
    // "000…" becomes empty after stripping leading zeros — still invalid.
    if (!local) {
      return false;
    }
    if (!/^\d{6,12}$/.test(local)) {
      return false;
    }
    // Reject obvious junk: all same digit, or trivial sequences.
    if (/^(\d)\1+$/.test(local)) {
      return false;
    }
    if (local === '1234567890' || local === '0123456789') {
      return false;
    }
    const dial = this.normalizeDialCode(dialCode);
    // India mobile: 10 digits starting 6–9.
    if (dial === '+91') {
      return /^[6-9]\d{9}$/.test(local);
    }
    return true;
  }

  isValidPhone(value: string): boolean {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return true;
    }
    const parts = this.parsePhoneParts(trimmed);
    if (!parts.national) {
      return false;
    }
    if (!this.isValidNational(parts.national, parts.dialCode)) {
      return false;
    }
    const e164 = this.composeE164(parts.dialCode, parts.national);
    const digits = e164.replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 15;
  }

  hasSavedPhone(user: any): boolean {
    const phone = this.normalizePhone(user?.meta?.phone_number);
    return !!phone && this.isValidPhone(phone);
  }

  dialCodeForCountry(countryCode?: string): string {
    const iso = String(countryCode || '').trim().toUpperCase();
    if (iso === 'UK') {
      return '+44';
    }
    const match = this.getCountries().find((c) => c.code === iso);
    return match ? this.normalizeDialCode(match.dialCode) : this.defaultDialCode;
  }

  toE164(value: unknown, countryHint?: string): string {
    const raw = this.normalizePhone(value);
    if (!raw) {
      return '';
    }
    const compact = raw.replace(/[^\d+]/g, '');
    if (compact.startsWith('+')) {
      const parts = this.parsePhoneParts(raw);
      const e164 = this.composeE164(parts.dialCode, parts.national);
      return e164 && this.isValidPhone(e164) ? e164 : '';
    }
    const national = this.normalizeNational(compact);
    const dial = this.dialCodeForCountry(countryHint);
    const e164 = this.composeE164(dial, national);
    return e164 && this.isValidPhone(e164) ? e164 : '';
  }

  parseDailyNumbers(raw: unknown): string[] {
    let list: unknown[] = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        list = Array.isArray(parsed) ? parsed : [];
      } catch {
        list = [];
      }
    }
    const out: string[] = [];
    for (const item of list) {
      const e164 = this.toE164(item);
      if (!e164 || out.includes(e164)) {
        continue;
      }
      out.push(e164);
      if (out.length >= 3) {
        break;
      }
    }
    return out;
  }

  /**
   * First saved workspace mobile becomes Daily Insights recipient #1.
   * Never overwrites an existing list. Never writes the other way.
   */
  seedDailyRecipientMeta(
    phoneE164: string,
    existingMeta: Record<string, unknown> | null | undefined
  ): Record<string, string> | null {
    if (!phoneE164) {
      return null;
    }
    const existing = this.parseDailyNumbers(existingMeta?.ss_whatsapp_daily_numbers);
    if (existing.length > 0) {
      return null;
    }
    return {
      ss_whatsapp_daily_opted_in: '1',
      ss_whatsapp_daily_numbers: JSON.stringify([phoneE164]),
    };
  }

  /**
   * Persist meta.phone_number as E.164 (e.g. +919876543210). Empty clears.
   * No OTP. Caller handles UI status / snackbars.
   */
  savePhone(
    trimmed: string,
    source: ContactPhoneSource
  ): Observable<any> {
    let value = String(trimmed || '').trim();
    if (value) {
      const parts = this.parsePhoneParts(value);
      value = this.composeE164(parts.dialCode, parts.national);
      if (!this.isValidPhone(value)) {
        return throwError(() => new Error('invalid_phone'));
      }
    }

    const current =
      this.userService.getCurrentUserValue() ||
      this.onboardingService.getCurrentUser() ||
      {};
    const meta: Record<string, string> = {
      phone_number: value,
    };
    if (value) {
      const seeded = this.seedDailyRecipientMeta(value, current?.meta);
      if (seeded) {
        Object.assign(meta, seeded);
      }
    }

    return this.userService.updateUser({ meta }).pipe(
      tap(() => {
        const latest =
          this.userService.getCurrentUserValue() ||
          this.onboardingService.getCurrentUser() ||
          current;
        const nextMeta = { ...(latest.meta || {}), ...meta };
        if (!value) {
          delete nextMeta.phone_number;
        }
        this.userService.setCurrentUser({
          ...latest,
          meta: nextMeta,
        });
        this.activationJourney.trackEvent('contact_phone_saved', {
          captured: !!value,
          source,
        });
      })
    );
  }

  trackSkipped(source: ContactPhoneSource): void {
    this.activationJourney.trackEvent('contact_phone_skipped', { source });
  }

  trackNudgeShown(source: ContactPhoneSource): void {
    this.activationJourney.trackEvent('contact_phone_nudge_shown', { source });
  }

  trackPopupSnoozed(untilDate: string): void {
    this.activationJourney.trackEvent('contact_phone_popup_snoozed', { untilDate });
  }
}
